import React, { useEffect, useMemo, useState } from 'react';
import axios from '../../helpers/axios';

const formatMoney = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;

const SubscriptionPlansModal = ({
    visible,
    onClose,
    onBuy,
    loading,
    balance = 0,
    targetModel = 'Office',
    targetTitle = '',
    currentSubscription = null,
}) => {
    const [plans, setPlans] = useState([]);
    const [officeBilling, setOfficeBilling] = useState(null);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plansError, setPlansError] = useState('');

    useEffect(() => {
        if (!visible) return undefined;

        let cancelled = false;
        setPlansLoading(true);
        setPlansError('');

        axios.get('/billing/subscription/plans')
            .then((res) => {
                if (cancelled) return;
                setPlans(Array.isArray(res.data?.plans) ? res.data.plans : []);
                setOfficeBilling(res.data?.officeBilling || null);
            })
            .catch((error) => {
                if (cancelled) return;
                setPlans([]);
                setOfficeBilling(null);
                setPlansError(
                    error?.response?.data?.error
                    || error?.response?.data?.message
                    || 'Не удалось загрузить настройки тарифа.'
                );
            })
            .finally(() => {
                if (!cancelled) setPlansLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [visible]);

    const visiblePlans = useMemo(
        () => plans.filter((plan) => plan?.targetModel === 'Office'),
        [plans]
    );

    if (!visible) return null;

    const hasActiveSubscription = !!(
        currentSubscription?.validUntil
        && currentSubscription?.type !== 'none'
        && currentSubscription?.status !== 'inactive'
        && currentSubscription?.status !== 'past_due'
        && new Date(currentSubscription.validUntil) > new Date()
    );

    const includedScenes = Math.max(0, Number(officeBilling?.includedScenes || 0));
    const maxWorkers = Math.max(0, Number(officeBilling?.maxWorkers || 0));
    const scenePrice = Math.max(0, Number(officeBilling?.extraScenePrice || 0));
    const periodDays = Math.max(0, Number(officeBilling?.periodDays || 0));
    const packages = Array.isArray(officeBilling?.scenePackages)
        ? officeBilling.scenePackages.filter((item) => item?.isActive !== false)
        : [];

    return (
        <div className="subscription-modal" onClick={onClose}>
            <div className="subscription-modal__body" onClick={(e) => e.stopPropagation()}>
                <div className="subscription-modal__header">
                    <div>
                        <h3>Подписка офиса</h3>
                        <p>{`Офис${targetTitle ? `: ${targetTitle}` : ''}. Оплатить может сотрудник с доступом к офису.`}</p>
                    </div>
                    <button className="subscription-modal__close" onClick={onClose}>✕</button>
                </div>

                <div className="subscription-modal__balance">
                    Баланс офиса: <b>{formatMoney(balance)}</b>
                </div>

                {plansLoading ? (
                    <div className="subscription-modal__notice">Загрузка актуального тарифа...</div>
                ) : plansError ? (
                    <div className="subscription-modal__notice">{plansError}</div>
                ) : officeBilling ? (
                    <div className="subscription-modal__notice">
                        В период включено {includedScenes} сцен. После них используются пакетные сцены,
                        затем новая сцена стоит {formatMoney(scenePrice)}. Лимит сотрудников — {maxWorkers}.
                    </div>
                ) : null}

                <div className="subscription-modal__plans">
                    {visiblePlans.map((plan) => {
                        const price = Number(officeBilling?.monthlyPrice ?? plan.price ?? 0);
                        const days = Number(officeBilling?.periodDays ?? plan.days ?? 0);
                        const disabled = loading || plansLoading || hasActiveSubscription || targetModel !== 'Office';

                        return (
                            <div className={hasActiveSubscription ? 'subscription-modal__plan current' : 'subscription-modal__plan'} key={plan.key}>
                                <div className="subscription-modal__plan-label">Офисная подписка</div>
                                <h4>{plan.title || 'Офисный тариф'}</h4>
                                <div className="subscription-modal__price">
                                    {formatMoney(price)}{days ? ` / ${days} дней` : ''}
                                </div>
                                <p className="subscription-modal__description">Полный рабочий доступ к офису по одному тарифу.</p>
                                <ul>
                                    <li>До {maxWorkers} сотрудников</li>
                                    <li>Заказы — без лимита</li>
                                    <li>Товары — без лимита</li>
                                    <li>{includedScenes} сцен включено на каждый период{periodDays ? ` (${periodDays} дней)` : ''}</li>
                                    <li>После включённых и пакетных сцен — {formatMoney(scenePrice)} за сцену</li>
                                    {packages.length ? (
                                        <li>
                                            Пакеты: {packages.map((item) => `${item.title || `${item.scenes} сцен`} — ${formatMoney(item.price)}`).join('; ')}
                                        </li>
                                    ) : null}
                                    <li>Автопродление с баланса офиса</li>
                                </ul>
                                <button
                                    className={disabled ? 'subscription-modal__buy disabled' : 'subscription-modal__buy'}
                                    disabled={disabled}
                                    onClick={() => onBuy(plan.key)}
                                >
                                    {loading ? 'Обработка...' : hasActiveSubscription ? 'Подписка уже активна' : 'Оплатить подписку'}
                                </button>
                            </div>
                        );
                    })}

                    {!plansLoading && !plansError && visiblePlans.length === 0 ? (
                        <div className="subscription-modal__notice">Сервер не вернул доступный офисный тариф.</div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPlansModal;
